import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/Toast";

export default function AccountSettingsPage() {
  const { user, refreshUser } = useAuth();
  const toast = useToast();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    if (user) {
      setName(user.full_name || user.first_name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    const errors = {};
    if (!email) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = "Invalid email";

    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setProfileErrors({});
    setIsSavingProfile(true);

    // TODO: needs backend - implement profile update API endpoint
    // dispatch(AuthActions.updateProfileRequest({ name, email }, callback))
    setTimeout(() => {
      setIsSavingProfile(false);
      toast.success("Profile updated successfully");
    }, 800);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    const errors = {};
    if (!currentPassword) errors.currentPassword = "Current password is required";
    if (!newPassword) errors.newPassword = "New password is required";
    else if (newPassword.length < 8) errors.newPassword = "Must be at least 8 characters";
    if (newPassword !== confirmPassword) errors.confirmPassword = "Passwords don't match";

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setPasswordErrors({});
    setIsSavingPassword(true);

    // TODO: needs backend - implement password change API endpoint via Devise
    // dispatch(AuthActions.changePasswordRequest({ currentPassword, newPassword }, callback))
    setTimeout(() => {
      setIsSavingPassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed successfully");
    }, 800);
  };

  const inputClass = (hasError) =>
    `w-full px-4 py-3 text-base border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none ${
      hasError ? "border-red-300 bg-red-50" : "border-gray-200"
    }`;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/"
          className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center gap-1 mb-4 min-h-[44px]"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Back
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
        <p className="text-gray-500 mt-1">Manage your profile and security</p>
      </div>

      {/* Profile Section */}
      <form onSubmit={handleProfileSubmit} className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile</h2>

        <div className="space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-4 mb-2">
            <div className="w-14 h-14 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xl font-semibold">
              {name ? name[0].toUpperCase() : email ? email[0].toUpperCase() : "U"}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{name || "User"}</p>
              <p className="text-xs text-gray-500">{email}</p>
            </div>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Full Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass(false)}
              placeholder="Your name"
            />
          </div>

          <div>
            <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Email Address
            </label>
            <input
              id="profile-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (profileErrors.email) setProfileErrors({});
              }}
              className={inputClass(profileErrors.email)}
              placeholder="you@example.com"
            />
            {profileErrors.email && (
              <p className="mt-1.5 text-sm text-red-500">{profileErrors.email}</p>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={isSavingProfile}
            className="px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {isSavingProfile ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </form>

      {/* Password Section */}
      <form onSubmit={handlePasswordSubmit} className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1.5">
              Current Password
            </label>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                if (passwordErrors.currentPassword) setPasswordErrors((prev) => ({ ...prev, currentPassword: "" }));
              }}
              className={inputClass(passwordErrors.currentPassword)}
              autoComplete="current-password"
            />
            {passwordErrors.currentPassword && (
              <p className="mt-1.5 text-sm text-red-500">{passwordErrors.currentPassword}</p>
            )}
          </div>

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1.5">
              New Password
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (passwordErrors.newPassword) setPasswordErrors((prev) => ({ ...prev, newPassword: "" }));
              }}
              className={inputClass(passwordErrors.newPassword)}
              autoComplete="new-password"
              placeholder="Min. 8 characters"
            />
            {passwordErrors.newPassword && (
              <p className="mt-1.5 text-sm text-red-500">{passwordErrors.newPassword}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1.5">
              Confirm New Password
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (passwordErrors.confirmPassword) setPasswordErrors((prev) => ({ ...prev, confirmPassword: "" }));
              }}
              className={inputClass(passwordErrors.confirmPassword)}
              autoComplete="new-password"
            />
            {passwordErrors.confirmPassword && (
              <p className="mt-1.5 text-sm text-red-500">{passwordErrors.confirmPassword}</p>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={isSavingPassword}
            className="px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {isSavingPassword ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Changing...
              </span>
            ) : (
              "Change Password"
            )}
          </button>
        </div>
      </form>

      {/* Plan info card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Subscription</h2>
        <p className="text-sm text-gray-500 mb-4">
          Manage your plan and billing from the Plans page.
        </p>
        <Link
          to="/plans"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          View Plans & Billing
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
